import { TApp } from '../types';

type TFeatureToggleConfig = {
  [K in TApp]: {
    [feature: string]: {
      default: boolean;
      env: string;
    };
  };
};

const FeatureToggleConfig: TFeatureToggleConfig = {
  'admin-portal': {
    isShowFaceComparisonResult: {
      default: false,
      env: 'NEXT_PUBLIC_FEATURE_TOGGLE_SHOW_FACE_COMPARISON_RESULT',
    },
    isAllowOverrideSomeDocumentDetailsWhenItVerified: {
      default: false,
      env: 'NEXT_PUBLIC_FEATURE_TOGGLE_ALLOW_OVERRIDE_SOME_DOCUMENT_DETAILS_WHEN_IT_VERIFIED',
    },
    isShowTrafficLight: {
      default: false,
      env: 'NEXT_PUBLIC_FEATURE_TOGGLE_SHOW_TRAFFIC_LIGHT',
    },
  },
  'client-portal': {
    isShowFaceComparisonResult: {
      default: false,
      env: 'NEXT_PUBLIC_FEATURE_TOGGLE_SHOW_FACE_COMPARISON_RESULT',
    },
    isShowTrafficLight: {
      default: false,
      env: 'NEXT_PUBLIC_FEATURE_TOGGLE_SHOW_TRAFFIC_LIGHT',
    },
    isShowWholesaleCertificateOnlyInClientPortal: {
      default: false,
      env: 'NEXT_PUBLIC_FEATURE_TOGGLE_SHOW_WHOLESALE_CERTIFICATE_ONLY_IN_CLIENT_PORTAL',
    },
  },
  'customer-portal': {
    isSetupCredentialNotAfterActivateTrackId: {
      default: false,
      env: 'NEXT_PUBLIC_FEATURE_TOGGLE_SETUP_CREDENTIAL_NOT_AFTER_ACTIVATE_TRACK_ID',
    },
    isSetupCredentialAllowSkip: {
      default: false,
      env: 'NEXT_PUBLIC_FEATURE_TOGGLE_SETUP_CREDENTIAL_ALLOW_SKIP',
    },
    isSyncUploadFaceCheckVideo: {
      default: false,
      env: 'NEXT_PUBLIC_FEATURE_TOGGLE_SYNC_UPLOAD_FACE_CHECK_VIDEO',
    },
    isPassiveFaceCheckEnabled: {
      default: false,
      env: 'NEXT_PUBLIC_FEATURE_TOGGLE_PASSIVE_FACE_CHECK_ENABLED',
    },
    isNewIdentityDocumentsApiForWizardEnabled: {
      default: false,
      env: 'NEXT_PUBLIC_FEATURE_TOGGLE_NEW_IDENTITY_DOCUMENTS_API_FOR_WIZARD_ENABLED',
    },
  },
} as const;

// Generate TFeatureToggle from FeatureToggleConfig
export type TFeatureToggle = {
  [K in keyof (typeof FeatureToggleConfig)[TApp]]: boolean;
};

// Due to known limitations of Next.js, we cannot use dynamic keys to access environment variables.
// Which means that `process.env[dynamicKey]` will not work as expected in the client-side code.
// Therefore, we statically read all relevant env configs at build time
const featureToggleEnv: Record<string, string | undefined> = {
  NEXT_PUBLIC_FEATURE_TOGGLE_SHOW_FACE_COMPARISON_RESULT:
    process.env.NEXT_PUBLIC_FEATURE_TOGGLE_SHOW_FACE_COMPARISON_RESULT,
  NEXT_PUBLIC_FEATURE_TOGGLE_ALLOW_OVERRIDE_SOME_DOCUMENT_DETAILS_WHEN_IT_VERIFIED:
    process.env.NEXT_PUBLIC_FEATURE_TOGGLE_ALLOW_OVERRIDE_SOME_DOCUMENT_DETAILS_WHEN_IT_VERIFIED,
  NEXT_PUBLIC_FEATURE_TOGGLE_SHOW_TRAFFIC_LIGHT:
    process.env.NEXT_PUBLIC_FEATURE_TOGGLE_SHOW_TRAFFIC_LIGHT,
  NEXT_PUBLIC_FEATURE_TOGGLE_SHOW_WHOLESALE_CERTIFICATE_ONLY_IN_CLIENT_PORTAL:
    process.env.NEXT_PUBLIC_FEATURE_TOGGLE_SHOW_WHOLESALE_CERTIFICATE_ONLY_IN_CLIENT_PORTAL,
  NEXT_PUBLIC_FEATURE_TOGGLE_SETUP_CREDENTIAL_NOT_AFTER_ACTIVATE_TRACK_ID:
    process.env.NEXT_PUBLIC_FEATURE_TOGGLE_SETUP_CREDENTIAL_NOT_AFTER_ACTIVATE_TRACK_ID,
  NEXT_PUBLIC_FEATURE_TOGGLE_SETUP_CREDENTIAL_ALLOW_SKIP:
    process.env.NEXT_PUBLIC_FEATURE_TOGGLE_SETUP_CREDENTIAL_ALLOW_SKIP,
  NEXT_PUBLIC_FEATURE_TOGGLE_SYNC_UPLOAD_FACE_CHECK_VIDEO:
    process.env.NEXT_PUBLIC_FEATURE_TOGGLE_SYNC_UPLOAD_FACE_CHECK_VIDEO,
  NEXT_PUBLIC_FEATURE_TOGGLE_PASSIVE_FACE_CHECK_ENABLED:
    process.env.NEXT_PUBLIC_FEATURE_TOGGLE_PASSIVE_FACE_CHECK_ENABLED,
  NEXT_PUBLIC_FEATURE_TOGGLE_NEW_IDENTITY_DOCUMENTS_API_FOR_WIZARD_ENABLED:
    process.env.NEXT_PUBLIC_FEATURE_TOGGLE_NEW_IDENTITY_DOCUMENTS_API_FOR_WIZARD_ENABLED,
};

function resolveToggleValue(env: string): boolean {
  return featureToggleEnv[env] === 'on';
}

export function useFeatureToggle() {
  const app = process.env.NEXT_PUBLIC_APP as TApp;
  if (!app) {
    return {} as TFeatureToggle;
  }

  const config = FeatureToggleConfig[app];
  if (!config) {
    return {} as TFeatureToggle;
  }

  const toggles = getFeatureToggleConfigWithResolvedValue(app);

  return Object.entries(toggles).reduce((acc, [key, config]) => {
    acc[key] = config.resolvedValue;
    return acc;
  }, {} as TFeatureToggle);
}

export type TFeatureToggleConfigDetails = {
  env: string;
  default: boolean;
  configuredValue: string | undefined;
  resolvedValue: boolean;
};

export type TFeatureToggleConfigWithResolvedValue = {
  [feature: string]: TFeatureToggleConfigDetails;
};

/**
 * Returns the feature toggle config for a given app,
 * with each feature toggle including a `configedValue` property
 * representing the configured env value (from env) and a `resolvedValue` property
 * representing the resolved value (from env or default).
 */
export function getFeatureToggleConfigWithResolvedValue(
  app: TApp,
): TFeatureToggleConfigWithResolvedValue {
  const config = FeatureToggleConfig[app];
  if (!config) {
    return {};
  }
  return Object.fromEntries(
    Object.entries(config).map(([feature, { default: defaultValue, env }]) => {
      return [
        feature,
        {
          env,
          default: defaultValue,
          configuredValue: featureToggleEnv[env],
          resolvedValue: resolveToggleValue(env) || defaultValue,
        },
      ];
    }),
  );
}
